/// <reference path="../../types/notifications.d.ts" />
import { X, Info, CircleX, CircleCheck, TriangleAlert } from 'lucide-react';
import type { Dispatch } from 'react';
import { useCallback, useEffect } from 'react';
import Button from '../../components/common/Button';
import { remove } from './actions';

export interface NotificationElementProps {
    notification: NotificationItem;
    dispatch: Dispatch<NotificationAction>;
}

const NotificationElement = ({ notification, dispatch }: NotificationElementProps) => {
    useEffect(() => {
        if (!notification.duration) {
            // do nothing
            return () => {};
        }

        let timeoutId: NodeJS.Timeout | null = setTimeout(() => {
            timeoutId = null;
            dispatch({ type: 'remove', notificationId: notification.id });
        }, notification.duration);

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [dispatch, notification]);

    const onClose = useCallback(() => remove(notification.id), [notification.id]);

    return (
        <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-leave:duration-100 data-leave:ease-in data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0">
            <div className="p-4" data-cy="notification">
                <div className="flex items-start">
                    <div className="shrink-0">
                        {notification.type === 'warning' && (
                            <TriangleAlert aria-hidden="true" className="text-warning h-6 w-6" />
                        )}
                        {notification.type === 'error' && (
                            <CircleX aria-hidden="true" className="text-destructive h-6 w-6" />
                        )}
                        {notification.type === 'success' && (
                            <CircleCheck aria-hidden="true" className="text-success h-6 w-6" />
                        )}
                        {notification.type === 'info' && <Info aria-hidden="true" className="text-info h-6 w-6" />}
                    </div>
                    <div className="ml-3 w-0 flex-1 pt-0.5">
                        <p className="text-sm font-medium text-gray-900">{notification.message}</p>
                        <p className="mt-1 text-sm text-gray-500">{notification.description}</p>
                    </div>
                    <div className="ml-4 flex shrink-0">
                        <Button size="icon" color="secondary" variant="ghost" onClick={onClose}>
                            <X aria-hidden="true" className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationElement;
